<script setup lang="ts">
import { computed, defineProps, reactive, ref, watch } from 'vue'

import { api, isFetchError } from '@/services'
import type { ForgotPasswordRequest, FormItemError } from '@/services/api'

import { useGlobalStore } from '@/stores/global'
import ErrorDisplayHelper from '@/components/helpers/ErrorDisplayHelper.vue'
import { required } from '@/utils/helpers-validation'

const globalStore = useGlobalStore()

interface Props {
  modalValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modalValue'])

const formRef = ref<HTMLFormElement | null>(null)
const loading = computed(() => {
  return globalStore.loadingCounter > 0
})

const form: ForgotPasswordRequest = reactive({
  email: ''
})

const errors = ref<FormItemError[]>([])
const haveErrors = computed(() => {
  return errors.value?.length > 0
})

const dialog = computed({
  get: () => props.modalValue,
  set: (value) => emit('update:modalValue', value)
})

watch(
  () => props.modalValue,
  (newVal) => {
    if (!newVal) {
      form.email = ''
    }
  }
)

const forgotPassword = async () => {
  const { valid } = await formRef.value!.validate()
  if (!valid) {
    return
  }

  let success = 'error'
  try {
    globalStore.incrementLoading()
    await api.api.requestPasswordReset({ data: form })
    success = 'success'
  } catch (error) {
    if (isFetchError(error)) {
      if (error.error.message) {
        if (typeof error.error.message === 'string') {
          errors.value = [
            {
              message: error.error.message
            }
          ]
        } else {
          errors.value = [...error.error.message]
        }
      }
    }
  } finally {
    globalStore.decrementLoading()
    if (success === 'success') {
      globalStore.addSnackbarMessageToQueue(
        "If your email address is in our system, you'll receive an email with further instructions",
        'info'
      )

      // add to snackbar queue...
      emit('update:modalValue', false)
    }
  }
}

function closeDialog() {
  emit('update:modalValue', false)
}
</script>
<template>
  <v-dialog v-model="dialog" class="dialog-modal-small" scrollable @keydown.esc="closeDialog">
    <v-card class="ma-0 pa-0 dialog-card" :loading="loading">
      <v-card-title class="text-white bg-lightblue d-flex justify-space-between align-center">
        <span class="card-title-content">Forgot Your Password?</span>
        <v-spacer />
        <v-icon class="text-white" icon="mdi-close" size="small" @click="closeDialog"></v-icon>
      </v-card-title>
      <v-card-text>
        <v-row
          ><v-col>
            <p>Just enter the email you signed up with and we'll send a password reset.</p>
          </v-col></v-row
        >

        <v-row>
          <v-col>
            <ErrorDisplayHelper :have-errors="haveErrors" :errors="errors"></ErrorDisplayHelper>
          </v-col>
        </v-row>

        <v-form ref="formRef" @submit.prevent="forgotPassword">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="form.email"
                :readonly="loading"
                :rules="[required]"
                class="mb-2"
                label="Email"
                clearable
                prepend-inner-icon="mdi-email-outline"
                variant="outlined"
                description="Make sure this is the email you signed up with."
              ></v-text-field>

              <br />Make sure this is the email you signed up with.
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <div class="d-flex justify-center align-content-center align-center">
                <v-btn
                  variant="elevated"
                  class="p-4 bg-lightblue text-white text-bold"
                  :disabled="!formRef"
                  :loading="loading"
                  type="submit"
                >
                  Send Reset
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-btn variant="elevated" class="p-4 bg-error text-bold" @click="closeDialog">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
